import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { formatDayLabel, formatUsd, formatUsdAxis, type TeamUsage } from '@/api-models/usage';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { buildSeries, stackDay, type Series } from './series';

/**
 * Daily spend, stacked by member.
 *
 * Two views, one axis each — never two scales on one plot. "Daily" answers *what
 * did we spend that day*; "Cumulative" answers *where is the cycle heading*.
 * Overlaying them on a shared plot with separate left/right scales, the usual
 * temptation, invites the reader to compare two things that aren't comparable, so
 * they're a toggle instead.
 */
type ChartView = 'daily' | 'cumulative';

const PLOT_HEIGHT = 232;
const PAD = { top: 14, right: 10, bottom: 26, left: 58 };
/** Cap from the mark spec — a bar never fills its band; the leftover is air. */
const MAX_BAR_WIDTH = 24;
/** Surface-colored gap that separates touching marks (stack segments, neighbours). */
const GAP = 2;
/** Floor a stack segment is never drawn below, so a small charge stays visible. */
const MIN_SEGMENT = 1.5;
const TICK_COUNT = 4;

interface HoverState {
  index: number;
  x: number;
  y: number;
}

export function UsageChart({ usage }: { usage: TeamUsage }) {
  const [view, setView] = useState<ChartView>('daily');
  const [hover, setHover] = useState<HoverState | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(wrapRef);

  const series = useMemo(() => buildSeries(usage), [usage]);

  /** Per-day stacks, and their running totals, in series order. */
  const stacks = useMemo(() => {
    const running = series.map(() => 0);
    return usage.daily.map((day) => {
      const values = stackDay(day.per_user, series);
      values.forEach((v, i) => {
        running[i]! += v;
      });
      return { date: day.date, values, cumulative: [...running], total: day.total_micro_usd };
    });
  }, [usage, series]);

  const max = useMemo(() => {
    const peak =
      view === 'daily'
        ? Math.max(0, ...stacks.map((s) => s.values.reduce((a, b) => a + Math.max(0, b), 0)))
        : Math.max(0, ...stacks.map((s) => s.cumulative.reduce((a, b) => a + Math.max(0, b), 0)));
    return niceMax(peak);
  }, [stacks, view]);

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const band = stacks.length > 0 ? plotW / stacks.length : 0;
  const barW = Math.max(1, Math.min(MAX_BAR_WIDTH, band - GAP * 2));
  const y = (micro: number) => PAD.top + PLOT_HEIGHT * (1 - (max === 0 ? 0 : micro / max));
  const bandCenter = (i: number) => PAD.left + band * (i + 0.5);

  const ticks = useMemo(() => {
    if (max === 0) return [0];
    return Array.from({ length: TICK_COUNT + 1 }, (_, i) => (max / TICK_COUNT) * i);
  }, [max]);

  // Which x labels fit without colliding: one every Nth band, at ~64px minimum.
  const labelStride = band > 0 ? Math.max(1, Math.ceil(64 / band)) : 1;

  const totalHeight = PAD.top + PLOT_HEIGHT + PAD.bottom;
  const hovered = hover ? stacks[hover.index] : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <Legend series={series} />
        <SegmentedControl
          value={view}
          onChange={setView}
          ariaLabel="Chart view"
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'cumulative', label: 'Cumulative' },
          ]}
        />
      </div>

      <div ref={wrapRef} className="relative w-full">
        {width > 0 && (
          <svg
            width={width}
            height={totalHeight}
            role="img"
            aria-label={`${view === 'daily' ? 'Daily' : 'Cumulative'} spend from ${usage.start} to ${usage.end}`}
            onMouseLeave={() => setHover(null)}
          >
            {/* Gridlines + value ticks — hairline, solid, recessive. */}
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={y(t)}
                  y2={y(t)}
                  stroke="var(--color-line)"
                  strokeWidth={1}
                />
                <text x={PAD.left - 10} y={y(t) + 4} textAnchor="end" className="fill-fg-muted text-4xs tabular-nums">
                  {formatUsdAxis(t)}
                </text>
              </g>
            ))}

            {view === 'daily' ? (
              stacks.map((day, i) => (
                <StackedColumn
                  key={day.date}
                  values={day.values}
                  series={series}
                  x={bandCenter(i) - barW / 2}
                  width={barW}
                  y={y}
                  baseline={y(0)}
                />
              ))
            ) : (
              <CumulativeAreas stacks={stacks} series={series} x={bandCenter} y={y} baseline={y(0)} />
            )}

            {/* Baseline sits above the marks so bars read as growing from it. */}
            <line
              x1={PAD.left}
              x2={PAD.left + plotW}
              y1={y(0)}
              y2={y(0)}
              stroke="var(--color-line-strong)"
              strokeWidth={1}
            />

            {stacks.map((day, i) =>
              i % labelStride === 0 ? (
                <text
                  key={day.date}
                  x={bandCenter(i)}
                  y={totalHeight - 8}
                  textAnchor="middle"
                  className="fill-fg-muted text-4xs"
                >
                  {formatDayLabel(day.date)}
                </text>
              ) : null,
            )}

            {/* Hover crosshair for the focused band. */}
            {hover !== null && (
              <line
                x1={bandCenter(hover.index)}
                x2={bandCenter(hover.index)}
                y1={PAD.top}
                y2={y(0)}
                stroke="var(--color-line-strong)"
                strokeWidth={1}
              />
            )}

            {/* Full-height hit targets — bigger than the marks, so thin bars and
                zero-spend days are still reachable. */}
            {stacks.map((day, i) => (
              <rect
                key={day.date}
                x={PAD.left + band * i}
                y={PAD.top}
                width={Math.max(1, band)}
                height={PLOT_HEIGHT}
                fill="transparent"
                onMouseEnter={() => setHover({ index: i, x: bandCenter(i), y: PAD.top })}
              />
            ))}
          </svg>
        )}

        {hovered && (
          <ChartTooltip
            date={hovered.date}
            series={series}
            values={view === 'daily' ? hovered.values : hovered.cumulative}
            containerWidth={width}
            x={hover!.x}
            label={view === 'daily' ? 'Spend' : 'Cumulative'}
          />
        )}
      </div>
    </div>
  );
}

/**
 * One day's bar. Segments are drawn from the baseline up, each shortened by the
 * surface gap, with only the topmost segment's cap rounded — the mark has one
 * data-end, and rounding interior joins would read as separate bars.
 */
function StackedColumn({
  values,
  series,
  x,
  width,
  y,
  baseline,
}: {
  values: number[];
  series: Series[];
  x: number;
  width: number;
  y: (micro: number) => number;
  baseline: number;
}) {
  let running = 0;
  const drawn = values
    .map((value, i) => {
      const from = running;
      running += Math.max(0, value);
      return { value, from, to: running, key: series[i]!.key, color: series[i]!.color };
    })
    .filter((seg) => seg.value > 0);

  return (
    <>
      {drawn.map((seg, i) => {
        const top = y(seg.to);
        const rawBottom = i === 0 ? baseline : y(seg.from);
        // The gap comes off the bottom of every segment except the one sitting on
        // the baseline, which must stay anchored to it. On a quiet day a segment
        // can be thinner than the gap itself — take only what leaves a visible
        // body behind, or the bar degenerates into a stack of hairlines and the
        // separator ends up louder than the data.
        const gap = i === 0 ? 0 : Math.min(GAP, Math.max(0, rawBottom - top - MIN_SEGMENT));
        const height = Math.max(MIN_SEGMENT, rawBottom - gap - top);
        // Only the top of the stack gets the rounded data-end; every other corner,
        // including the one on the baseline, stays square.
        const radius = i === drawn.length - 1 ? Math.min(4, width / 2, height) : 0;
        return <path key={seg.key} d={topRoundedRect(x, top, width, height, radius)} fill={seg.color} />;
      })}
    </>
  );
}

/** A rect with only its top two corners rounded. */
function topRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  if (r <= 0) return `M${x},${y} h${w} v${h} h${-w} Z`;
  return `M${x},${y + r} a${r},${r} 0 0 1 ${r},${-r} h${w - r * 2} a${r},${r} 0 0 1 ${r},${r} v${h - r} h${-w} Z`;
}

/**
 * Cumulative spend as stacked areas — each member's running contribution to the
 * window's total. The band boundaries are re-drawn as surface-colored strokes so
 * touching fills are separated by the same 2px gap the bars use.
 */
function CumulativeAreas({
  stacks,
  series,
  x,
  y,
  baseline,
}: {
  stacks: { cumulative: number[] }[];
  series: Series[];
  x: (i: number) => number;
  y: (micro: number) => number;
  baseline: number;
}) {
  if (stacks.length === 0) return null;

  // Running upper edge of each band, bottom-most series first.
  const edges: number[][] = [];
  const running = stacks.map(() => 0);
  for (let s = 0; s < series.length; s++) {
    edges.push(
      stacks.map((day, i) => {
        running[i]! += Math.max(0, day.cumulative[s] ?? 0);
        return running[i]!;
      }),
    );
  }

  const line = (points: number[]) => points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ');

  return (
    <>
      {series.map((s, idx) => {
        const upper = edges[idx]!;
        const lower = idx === 0 ? null : edges[idx - 1]!;
        const back = lower
          ? [...lower].map((v, i) => `L${x(lower.length - 1 - i)},${y(lower[lower.length - 1 - i]!)}`).join(' ')
          : `L${x(stacks.length - 1)},${baseline} L${x(0)},${baseline}`;
        return <path key={s.key} d={`${line(upper)} ${back} Z`} fill={s.color} />;
      })}
      {/* The gap: a surface-colored stroke along every interior boundary. */}
      {series.slice(0, -1).map((s, idx) => (
        <path
          key={`gap-${s.key}`}
          d={line(edges[idx]!)}
          fill="none"
          stroke="var(--color-surface-2)"
          strokeWidth={GAP}
          strokeLinejoin="round"
        />
      ))}
      {/* End marker on the top of the stack — where the cycle currently stands. */}
      <circle
        cx={x(stacks.length - 1)}
        cy={y(edges[edges.length - 1]?.[stacks.length - 1] ?? 0)}
        r={4}
        fill={series[series.length - 1]?.color ?? 'var(--color-series-1)'}
        stroke="var(--color-surface-2)"
        strokeWidth={GAP}
      />
    </>
  );
}

function Legend({ series }: { series: Series[] }) {
  // One series is self-describing — the section title already names it.
  if (series.length < 2) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {series.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5 text-xs text-fg-secondary">
          <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
          <span className="max-w-40 truncate">{s.label}</span>
        </span>
      ))}
    </div>
  );
}

function ChartTooltip({
  date,
  series,
  values,
  x,
  containerWidth,
  label,
}: {
  date: string;
  series: Series[];
  values: number[];
  x: number;
  containerWidth: number;
  label: string;
}) {
  const rows = series.map((s, i) => ({ series: s, value: values[i] ?? 0 })).filter((r) => r.value !== 0);
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  // Flip to the left of the crosshair when the tooltip would run off the edge.
  const flip = x > containerWidth - 200;

  return (
    <div
      className="pointer-events-none absolute top-2 z-10 min-w-44 rounded-lg border border-line bg-surface-4 p-2.5 shadow-lg"
      style={flip ? { right: containerWidth - x + 10 } : { left: x + 10 }}
    >
      <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
        {formatDayLabel(date, { withYear: true })}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-fg-tertiary">No spend</p>
      ) : (
        <>
          {rows.map((r) => (
            <div key={r.series.key} className="flex items-center justify-between gap-4 py-0.5">
              <span className="flex min-w-0 items-center gap-1.5">
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: r.series.color }}
                />
                <span className="truncate text-xs text-fg-secondary">{r.series.label}</span>
              </span>
              <span className="shrink-0 text-xs tabular-nums text-fg">{formatUsd(r.value)}</span>
            </div>
          ))}
          <div className="mt-1.5 flex items-center justify-between gap-4 border-t border-line-subtle pt-1.5">
            <span className="text-xs text-fg-tertiary">{label}</span>
            <span className="text-xs font-semibold tabular-nums text-fg">{formatUsd(total)}</span>
          </div>
        </>
      )}
    </div>
  );
}

/** Round an axis maximum up to a clean 1/2/5 × 10ⁿ step. */
function niceMax(value: number): number {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/** Track a container's width so the SVG can lay out in real pixels (no scaled text). */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}
